import { useState, useRef, useEffect } from "react";
import { loadStripeTerminal, type Terminal } from "@stripe/terminal-js";
import { Link } from "react-router-dom";
import {
  getOrCreateApptBill,
  createStandaloneBill,
  addBillLine,
  removeBillLine,
  setBillTip,
  markBillPaid,
  createCheckout,
  createTerminalPaymentIntent,
  getTerminalConfig,
  terminalConnectionToken,
  getCustomers,
  getProducts,
  getTaxConfig,
  type Bill,
  type BillLineItem,
  type Product,
} from "../lib/api.ts";

interface Props {
  slug: string;
  appointmentId?: string;
  standalone?: boolean;
  onClose: () => void;
  onDone: () => void;
}

type Screen = "typeahead" | "loading" | "bill" | "link" | "terminal" | "done";
type TerminalPhase = "connecting" | "collecting" | "done" | "error";
type PayMethod = "link" | "reader" | "cash" | "owing";
type CashMethod = "cash" | "bank" | "other";

interface CustomerLite {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  visit_count?: number | null;
}

function fmtCents(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

export function CheckoutModal({
  slug,
  appointmentId,
  standalone,
  onClose,
  onDone,
}: Props) {
  const [screen, setScreen] = useState<Screen>(standalone ? "typeahead" : "loading");
  const [error, setError] = useState<string | null>(null);

  // Bill state
  const [bill, setBill] = useState<Bill | null>(null);
  const [lineItems, setLineItems] = useState<BillLineItem[]>([]);

  // Terminal
  const [locationId, setLocationId] = useState<string | null>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const clientSecretRef = useRef<string | null>(null);
  const [terminalPhase, setTerminalPhase] = useState<TerminalPhase>("connecting");
  const [terminalStatus, setTerminalStatus] = useState("");

  // Payment method
  const [method, setMethod] = useState<PayMethod>("link");
  const [cashMethod, setCashMethod] = useState<CashMethod>("cash");

  // Add-line form
  const [showAddLine, setShowAddLine] = useState(false);
  const [addLabel, setAddLabel] = useState("");
  const [addAmount, setAddAmount] = useState("");
  const [addTaxable, setAddTaxable] = useState(false);
  const [addingLine, setAddingLine] = useState(false);

  // Product catalog picker
  const [products, setProducts] = useState<Product[]>([]);
  const [productQuery, setProductQuery] = useState("");

  // Tip (dollars string)
  const [tipDollars, setTipDollars] = useState("0.00");

  // Collecting spinner
  const [collecting, setCollecting] = useState(false);

  // Payment link URL
  const [payUrl, setPayUrl] = useState<string | null>(null);

  // Customer typeahead (standalone path)
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [suggestions, setSuggestions] = useState<CustomerLite[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestTimer = useRef<number | null>(null);

  // Tax config — drives the "Set up tax →" discoverability link.
  // Default true → no link until we know tax is actually off.
  const [taxEnabled, setTaxEnabled] = useState(true);

  // ── Load bill on mount (appointment path) ─────────────────────────────────
  useEffect(() => {
    if (standalone || !appointmentId) return;
    let cancelled = false;
    void (async () => {
      setScreen("loading");
      setError(null);
      try {
        const resp = await getOrCreateApptBill(slug, appointmentId);
        if (cancelled) return;
        setBill(resp.bill);
        setLineItems(resp.line_items ?? []);
        setTipDollars((resp.bill.tip_cents / 100).toFixed(2));
        const tc = await getTerminalConfig(slug).catch(() => ({ location_id: null }));
        if (cancelled) return;
        setLocationId(tc.location_id);
        setMethod(tc.location_id ? "reader" : "link");
        setScreen("bill");
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? "Could not load bill");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, appointmentId, standalone]);

  // Best-effort disconnect on unmount
  useEffect(() => {
    return () => {
      terminalRef.current?.disconnectReader?.().catch(() => {});
    };
  }, []);

  // ── Customer search debounce (standalone path) ────────────────────────────
  useEffect(() => {
    if (customerId) return;
    if (!customerQuery || customerQuery.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    if (suggestTimer.current) window.clearTimeout(suggestTimer.current);
    suggestTimer.current = window.setTimeout(() => {
      getCustomers(slug, customerQuery.trim())
        .then((rows) => setSuggestions(((rows as CustomerLite[]) ?? []).slice(0, 6)))
        .catch(() => setSuggestions([]));
    }, 200);
    return () => {
      if (suggestTimer.current) window.clearTimeout(suggestTimer.current);
    };
  }, [customerQuery, customerId, slug]);

  // Load tenant tax config once — independent of appointment/standalone path.
  useEffect(() => {
    let cancelled = false;
    void getTaxConfig(slug)
      .then((t) => {
        if (!cancelled) setTaxEnabled(t.tax_enabled);
      })
      .catch(() => {
        /* leave default; link simply won't show */
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  function pickCustomer(c: CustomerLite) {
    setCustomerId(c.id);
    setCustomerName(c.name);
    setCustomerQuery(c.name);
    setShowSuggestions(false);
  }

  // ── Standalone: create bill after customer selection ──────────────────────
  async function proceedStandalone() {
    setScreen("loading");
    setError(null);
    try {
      const resp = await createStandaloneBill(slug, customerId ?? undefined);
      setBill(resp.bill);
      setLineItems(resp.line_items ?? []);
      setTipDollars((resp.bill.tip_cents / 100).toFixed(2));
      const tc = await getTerminalConfig(slug).catch(() => ({ location_id: null }));
      setLocationId(tc.location_id);
      setMethod(tc.location_id ? "reader" : "link");
      setScreen("bill");
    } catch (e) {
      setError((e as Error).message ?? "Could not create bill");
      setScreen("typeahead");
    }
  }

  // ── Load active products when bill screen is shown ────────────────────────
  useEffect(() => {
    if (screen !== "bill") return;
    getProducts(slug, { activeOnly: true })
      .then((rows) => setProducts(rows))
      .catch(() => {});
  }, [slug, screen]);

  // ── Add line ──────────────────────────────────────────────────────────────
  async function handleAddProductLine(p: Product) {
    if (!bill) return;
    setAddingLine(true);
    setError(null);
    try {
      const resp = await addBillLine(slug, bill.id, {
        kind: "product",
        product_id: p.id,
        label: p.name,
        unit_amount_cents: p.price_cents,
        qty: 1,
        taxable: p.taxable === 1,
      });
      setBill(resp.bill);
      setLineItems(resp.line_items ?? []);
      setShowAddLine(false);
      setProductQuery("");
    } catch (e) {
      setError((e as Error).message ?? "Could not add line");
    } finally {
      setAddingLine(false);
    }
  }

  async function handleAddLine(e: React.FormEvent) {
    e.preventDefault();
    if (!bill) return;
    const cents = Math.round(parseFloat(addAmount) * 100);
    if (!addLabel.trim() || !Number.isFinite(cents) || cents <= 0) {
      setError("Enter a label and a positive amount");
      return;
    }
    setAddingLine(true);
    setError(null);
    try {
      const resp = await addBillLine(slug, bill.id, {
        kind: "adhoc",
        label: addLabel.trim(),
        unit_amount_cents: cents,
        qty: 1,
        taxable: addTaxable,
      });
      setBill(resp.bill);
      setLineItems(resp.line_items ?? []);
      setAddLabel("");
      setAddAmount("");
      setAddTaxable(false);
      setShowAddLine(false);
    } catch (e) {
      setError((e as Error).message ?? "Could not add line");
    } finally {
      setAddingLine(false);
    }
  }

  async function handleRemoveLine(lineId: string) {
    if (!bill) return;
    setError(null);
    try {
      const resp = await removeBillLine(slug, bill.id, lineId);
      setBill(resp.bill);
      setLineItems(resp.line_items ?? []);
    } catch (e) {
      setError((e as Error).message ?? "Could not remove line");
    }
  }

  async function handleTipBlur() {
    if (!bill) return;
    const tipCents = Math.round(parseFloat(tipDollars) * 100);
    if (!Number.isFinite(tipCents) || tipCents < 0) return;
    try {
      const resp = await setBillTip(slug, bill.id, tipCents);
      setBill(resp.bill);
    } catch (e) {
      setError((e as Error).message ?? "Could not update tip");
    }
  }

  const balance = bill ? bill.total_cents - bill.amount_paid_cents : 0;
  const cur = bill?.currency ?? "usd";

  const filteredProducts = productQuery.trim().length === 0
    ? products.slice(0, 8)
    : products
        .filter((p) => p.name.toLowerCase().includes(productQuery.trim().toLowerCase()))
        .slice(0, 8);

  // ── Terminal: no-double-charge helpers ────────────────────────────────────
  // Persist terminal + client_secret across retries. If the PI already exists,
  // retry re-collects against it — never creates a second charge.
  async function ensureReader(): Promise<Terminal> {
    if (terminalRef.current) return terminalRef.current;
    const StripeTerminal = await loadStripeTerminal();
    if (!StripeTerminal) throw new Error("Terminal SDK failed to load");
    const locId = locationId;
    if (!locId) throw new Error("No terminal location configured");
    const terminal = StripeTerminal.create({
      onFetchConnectionToken: async () => (await terminalConnectionToken(slug)).secret,
      onUnexpectedReaderDisconnect: () => {
        terminalRef.current = null;
        setError("Reader disconnected");
        setTerminalPhase("error");
      },
    });
    const discovery = await terminal.discoverReaders({ location: locId });
    if ("error" in discovery) throw new Error(discovery.error.message);
    if (!discovery.discoveredReaders.length)
      throw new Error("No reader found. Make sure it's powered on and online.");
    const connectResult = await terminal.connectReader(discovery.discoveredReaders[0]);
    if ("error" in connectResult) throw new Error(connectResult.error.message);
    terminalRef.current = terminal;
    return terminal;
  }

  async function collectAndProcess() {
    const terminal = terminalRef.current;
    const clientSecret = clientSecretRef.current;
    if (!terminal || !clientSecret) throw new Error("No active payment to collect");
    setTerminalPhase("collecting");
    setTerminalStatus("Tap, insert, or swipe the card on the reader…");
    const collect = await terminal.collectPaymentMethod(clientSecret);
    if ("error" in collect) throw new Error(collect.error.message);
    const process = await terminal.processPayment(collect.paymentIntent);
    if ("error" in process) throw new Error(process.error.message);
    setTerminalPhase("done");
    setTerminalStatus("Payment captured ✓");
    onDone();
    setScreen("done");
  }

  async function startTerminal() {
    if (!bill) return;
    setScreen("terminal");
    setTerminalPhase("connecting");
    setTerminalStatus("Connecting to reader…");
    setError(null);
    try {
      await ensureReader();
      const { client_secret } = await createTerminalPaymentIntent(slug, {
        amount_cents: balance,
        bill_id: bill.id,
        appointment_id: appointmentId,
        customer_id: bill.customer_id ?? undefined,
        kind: "appointment",
      });
      clientSecretRef.current = client_secret;
      await collectAndProcess();
    } catch (e) {
      setError((e as Error).message ?? "Terminal payment failed");
      setTerminalPhase("error");
    }
  }

  async function retryTerminal() {
    setError(null);
    if (clientSecretRef.current && terminalRef.current) {
      try {
        await collectAndProcess();
      } catch (e) {
        setError((e as Error).message ?? "Terminal payment failed");
        setTerminalPhase("error");
      }
    } else {
      void startTerminal();
    }
  }

  // ── Collect dispatch ──────────────────────────────────────────────────────
  async function handleCollect() {
    if (!bill) return;
    if (method === "link") {
      setCollecting(true);
      setError(null);
      try {
        const res = await createCheckout(slug, {
          amount_cents: balance,
          bill_id: bill.id,
          appointment_id: appointmentId,
          customer_id: bill.customer_id ?? undefined,
          kind: "appointment",
        });
        setPayUrl(res.url);
        setScreen("link");
        onDone();
      } catch (e) {
        setError((e as Error).message ?? "Could not create payment link");
      } finally {
        setCollecting(false);
      }
    } else if (method === "reader") {
      void startTerminal();
    } else if (method === "cash") {
      setCollecting(true);
      setError(null);
      try {
        await markBillPaid(slug, bill.id, balance, cashMethod);
        onDone();
        setScreen("done");
      } catch (e) {
        setError((e as Error).message ?? "Could not record payment");
      } finally {
        setCollecting(false);
      }
    } else if (method === "owing") {
      onDone();
      onClose();
    }
  }

  const busy = collecting;
  const terminalBusy = terminalPhase === "connecting" || terminalPhase === "collecting";
  // Only block closing while a card-terminal charge is actually running.
  // terminalPhase defaults to "connecting", so guarding on terminalBusy alone
  // would disable the ✕ / backdrop on every non-terminal screen.
  const terminalInProgress = screen === "terminal" && terminalBusy;

  // ── Totals rows ───────────────────────────────────────────────────────────
  const totalsRows: { label: string; value: string; bold: boolean }[] = bill
    ? [
        { label: "Subtotal", value: fmtCents(bill.subtotal_cents, cur), bold: false },
        { label: "Tax", value: fmtCents(bill.tax_cents, cur), bold: false },
        { label: "Tip", value: fmtCents(bill.tip_cents, cur), bold: false },
        { label: "Total", value: fmtCents(bill.total_cents, cur), bold: true },
        ...(bill.amount_paid_cents > 0
          ? [{ label: "Paid", value: `−${fmtCents(bill.amount_paid_cents, cur)}`, bold: false }]
          : []),
      ]
    : [];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center">
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.55)" }}
        onClick={busy || terminalInProgress ? undefined : onClose}
      />
      <div
        className="relative w-full md:max-w-lg rounded-t-2xl md:rounded-2xl max-h-[92vh] overflow-y-auto"
        style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <h2 style={{ fontSize: "1.05rem", fontWeight: 600, color: "var(--text)" }}>
            {screen === "done" ? "Payment complete" : "Checkout"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy || terminalInProgress}
            className="text-lg"
            style={{ color: "var(--text-muted)", cursor: "pointer", padding: "6px 8px", lineHeight: 1, borderRadius: 8 }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-3">
          {/* ── Typeahead (standalone) ── */}
          {screen === "typeahead" && (
            <>
              <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
                Search for an existing customer or continue as a walk-in.
              </p>
              <div style={{ position: "relative" }}>
                <input
                  className="input"
                  value={customerQuery}
                  onChange={(e) => {
                    setCustomerQuery(e.target.value);
                    if (customerId) {
                      setCustomerId(null);
                      setCustomerName("");
                    }
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => window.setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder="Customer name…"
                  autoFocus
                  autoComplete="off"
                />
                {showSuggestions && !customerId && suggestions.length > 0 && (
                  <div
                    style={{
                      marginTop: 4,
                      background: "var(--paper-raised)",
                      border: "1px solid var(--rule)",
                      borderRadius: 8,
                      boxShadow: "var(--shadow-md)",
                      padding: 4,
                      maxHeight: 240,
                      overflowY: "auto",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        fontSize: 10,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "var(--ink-faint)",
                        padding: "6px 10px 4px",
                      }}
                    >
                      Existing customers
                    </div>
                    {suggestions.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          pickCustomer(c);
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "left",
                          padding: "8px 10px",
                          background: "transparent",
                          border: "none",
                          borderRadius: 6,
                          fontSize: 13,
                          color: "var(--ink)",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "var(--paper-well)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 2 }}>
                          {[c.phone, c.email].filter(Boolean).join(" · ") || "no contact info"}
                          {typeof c.visit_count === "number" && c.visit_count > 0 && (
                            <span style={{ marginLeft: 8 }}>· {c.visit_count} visits</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {customerId && (
                <div style={{ fontSize: 12, color: "var(--accent-ink)" }}>
                  Linked: {customerName} ·{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerId(null);
                      setCustomerName("");
                      setCustomerQuery("");
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--accent-ink)",
                      cursor: "pointer",
                      textDecoration: "underline",
                      fontSize: 12,
                    }}
                  >
                    clear
                  </button>
                </div>
              )}
              {error && <div className="alert-error">{error}</div>}
            </>
          )}

          {/* ── Loading ── */}
          {screen === "loading" && (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              {error ? (
                <div className="alert-error">{error}</div>
              ) : (
                <span style={{ color: "var(--text-muted)", fontSize: 14 }}>Loading…</span>
              )}
            </div>
          )}

          {/* ── Bill ── */}
          {screen === "bill" && bill && (
            <>
              {/* Line items */}
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: 6,
                  }}
                >
                  Items
                </div>
                {lineItems.length === 0 && (
                  <div style={{ fontSize: 13, color: "var(--text-muted)", padding: "4px 0" }}>
                    No items yet.
                  </div>
                )}
                {lineItems.map((li) => (
                  <div
                    key={li.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 0",
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    <div style={{ flex: 1, fontSize: 13 }}>
                      <span style={{ fontWeight: 500 }}>{li.label}</span>
                      {li.qty > 1 && (
                        <span style={{ color: "var(--text-muted)", marginLeft: 4 }}>× {li.qty}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500, minWidth: 70, textAlign: "right" }}>
                      {fmtCents(li.amount_cents, cur)}
                    </div>
                    {li.kind !== "service" ? (
                      <button
                        type="button"
                        onClick={() => void handleRemoveLine(li.id)}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "var(--text-muted)",
                          cursor: "pointer",
                          fontSize: 13,
                          padding: "0 2px",
                          lineHeight: 1,
                        }}
                        aria-label={`Remove ${li.label}`}
                      >
                        ✕
                      </button>
                    ) : (
                      <div style={{ width: 18 }} />
                    )}
                  </div>
                ))}
              </div>

              {/* Add line */}
              {!showAddLine ? (
                <button
                  type="button"
                  onClick={() => setShowAddLine(true)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 13,
                    color: "var(--accent-ink)",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    padding: "2px 0",
                  }}
                >
                  + Add line
                </button>
              ) : (
                <form
                  onSubmit={(e) => void handleAddLine(e)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    background: "rgba(0,0,0,0.03)",
                    borderRadius: 8,
                    padding: 10,
                  }}
                >
                  {/* Product catalog picker */}
                  {products.length > 0 && (
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "var(--text-muted)",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          marginBottom: 4,
                        }}
                      >
                        From catalog
                      </div>
                      <input
                        className="input"
                        placeholder="Search products…"
                        value={productQuery}
                        onChange={(e) => setProductQuery(e.target.value)}
                        autoComplete="off"
                      />
                      {filteredProducts.length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 4 }}>
                          {filteredProducts.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => void handleAddProductLine(p)}
                              disabled={addingLine}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                                textAlign: "left",
                                padding: "7px 8px",
                                background: "var(--card-bg)",
                                border: "1px solid var(--card-border)",
                                borderRadius: 6,
                                fontSize: 13,
                                color: "var(--text)",
                                cursor: "pointer",
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>{p.name}</span>
                              <span style={{ color: "var(--text-muted)", fontSize: 12 }}>
                                {fmtCents(p.price_cents, cur)}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                      {productQuery.trim().length > 0 && filteredProducts.length === 0 && (
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                          No products match.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Divider between catalog and custom */}
                  {products.length > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>or custom</span>
                      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 6 }}>
                    <input
                      className="input"
                      placeholder="Label"
                      value={addLabel}
                      onChange={(e) => setAddLabel(e.target.value)}
                      style={{ flex: 2 }}
                      autoFocus={products.length === 0}
                    />
                    <input
                      className="input"
                      placeholder="Amount"
                      value={addAmount}
                      onChange={(e) => setAddAmount(e.target.value)}
                      inputMode="decimal"
                      style={{ flex: 1 }}
                    />
                  </div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 14,
                      cursor: "pointer",
                      padding: "12px 4px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={addTaxable}
                      onChange={(e) => setAddTaxable(e.target.checked)}
                      style={{ width: 20, height: 20, accentColor: "var(--ink)" }}
                    />
                    Taxable
                  </label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ flex: 1, fontSize: 12.5 }}
                      onClick={() => {
                        setShowAddLine(false);
                        setAddLabel("");
                        setAddAmount("");
                        setAddTaxable(false);
                        setProductQuery("");
                        setError(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      style={{ flex: 1, fontSize: 12.5 }}
                      disabled={addingLine}
                    >
                      {addingLine ? "Adding…" : "Add"}
                    </button>
                  </div>
                </form>
              )}

              {/* Tip */}
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <label
                  style={{ fontSize: 13, fontWeight: 500, minWidth: 30, color: "var(--text)" }}
                >
                  Tip
                </label>
                <input
                  className="input"
                  value={tipDollars}
                  onChange={(e) => setTipDollars(e.target.value)}
                  onBlur={() => void handleTipBlur()}
                  inputMode="decimal"
                  style={{ maxWidth: 90 }}
                />
              </div>

              {/* Totals */}
              <div
                style={{
                  background: "rgba(0,0,0,0.03)",
                  borderRadius: 8,
                  padding: "10px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 5,
                }}
              >
                {totalsRows.map(({ label, value, bold }) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: bold ? 14 : 13,
                      fontWeight: bold ? 600 : 400,
                    }}
                  >
                    <span style={{ color: bold ? "var(--text)" : "var(--text-muted)" }}>{label}</span>
                    {label === "Tax" && !taxEnabled ? (
                      <Link
                        to={`/${slug}/settings?tab=payments`}
                        onClick={onClose}
                        style={{
                          fontSize: 12.5,
                          color: "var(--accent-ink)",
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                      >
                        Set up tax →
                      </Link>
                    ) : (
                      <span style={{ color: "var(--text)" }}>{value}</span>
                    )}
                  </div>
                ))}
                {bill.amount_paid_cents > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 14,
                      fontWeight: 700,
                      borderTop: "1px solid var(--border)",
                      marginTop: 4,
                      paddingTop: 4,
                      color: "var(--text)",
                    }}
                  >
                    <span>Balance due</span>
                    <span>{fmtCents(balance, cur)}</span>
                  </div>
                )}
              </div>

              {/* Method selector — only when there is a balance */}
              {balance > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    How to collect
                  </div>
                  {([
                    ...(locationId ? [{ value: "reader" as const, label: "Card reader" }] : []),
                    { value: "link" as const, label: "Payment link" },
                    { value: "cash" as const, label: "Cash / Other" },
                    { value: "owing" as const, label: "Leave owing" },
                  ]).map((opt) => {
                    const selected = method === opt.value;
                    return (
                      <div key={opt.value}>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            fontSize: 14,
                            cursor: "pointer",
                            color: "var(--text)",
                            padding: "12px 14px",
                            borderRadius: 10,
                            border: `1px solid ${selected ? "var(--accent)" : "var(--border)"}`,
                            background: selected ? "var(--accent-soft)" : "transparent",
                          }}
                        >
                          <input
                            type="radio"
                            name="paymethod"
                            value={opt.value}
                            checked={selected}
                            onChange={() => setMethod(opt.value)}
                            style={{ width: 18, height: 18, accentColor: "var(--ink)" }}
                          />
                          {opt.label}
                        </label>
                        {opt.value === "cash" && selected && (
                          <select
                            className="input"
                            value={cashMethod}
                            onChange={(e) => setCashMethod(e.target.value as CashMethod)}
                            style={{ marginTop: 6 }}
                          >
                            <option value="cash">Cash</option>
                            <option value="bank">Bank transfer</option>
                            <option value="other">Other</option>
                          </select>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {error && <div className="alert-error">{error}</div>}
            </>
          )}

          {/* ── Payment link ── */}
          {screen === "link" && payUrl && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ fontSize: 14 }}>Payment link ready. Share it with the customer:</p>
              <input
                className="input"
                readOnly
                value={payUrl}
                onFocus={(e) => e.currentTarget.select()}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ flex: 1 }}
                  onClick={() => navigator.clipboard.writeText(payUrl)}
                >
                  Copy link
                </button>
                <a
                  className="btn-primary"
                  style={{ flex: 1, textAlign: "center" }}
                  href={payUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open checkout
                </a>
              </div>
            </div>
          )}

          {/* ── Terminal ── */}
          {screen === "terminal" && (
            <>
              {terminalBusy && (
                <div style={{ padding: "24px 0", textAlign: "center" }}>
                  <div style={{ fontSize: 14, color: "var(--text-muted)" }}>{terminalStatus}</div>
                </div>
              )}
              {terminalPhase === "done" && !terminalBusy && (
                <div style={{ padding: "16px 0", textAlign: "center" }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>
                    {terminalStatus}
                  </div>
                  <button
                    type="button"
                    className="btn-primary"
                    style={{ marginTop: 12 }}
                    onClick={onClose}
                  >
                    Done
                  </button>
                </div>
              )}
              {terminalPhase === "error" && (
                <>
                  <div className="alert-error">{error}</div>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => void retryTerminal()}
                  >
                    Try again
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setScreen("bill");
                      setError(null);
                    }}
                  >
                    Back
                  </button>
                </>
              )}
            </>
          )}

          {/* ── Done ── */}
          {screen === "done" && (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>
                Payment recorded
              </div>
              <button
                type="button"
                className="btn-primary"
                style={{ marginTop: 16 }}
                onClick={onClose}
              >
                Close
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {screen === "typeahead" && (
          <div className="flex gap-2 px-5 py-3" style={{ borderTop: "1px solid var(--border)" }}>
            <button
              type="button"
              className="btn-secondary"
              style={{ flex: 1 }}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              style={{ flex: 1 }}
              onClick={() => void proceedStandalone()}
            >
              {customerId ? `Continue with ${customerName}` : "Continue as walk-in"}
            </button>
          </div>
        )}
        {screen === "bill" && bill && balance > 0 && (
          <div className="flex gap-2 px-5 py-3" style={{ borderTop: "1px solid var(--border)" }}>
            <button
              type="button"
              className="btn-secondary"
              style={{ flex: 1 }}
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              style={{ flex: 1 }}
              onClick={() => void handleCollect()}
              disabled={busy}
            >
              {busy
                ? "Processing…"
                : method === "owing"
                  ? "Leave owing"
                  : `Collect ${fmtCents(balance, cur)}`}
            </button>
          </div>
        )}
        {screen === "bill" && bill && balance <= 0 && (
          <div className="flex gap-2 px-5 py-3" style={{ borderTop: "1px solid var(--border)" }}>
            <button
              type="button"
              className="btn-secondary"
              style={{ flex: 1 }}
              onClick={onClose}
            >
              Close
            </button>
            <button
              type="button"
              className="btn-primary"
              style={{ flex: 1 }}
              onClick={() => {
                onDone();
                onClose();
              }}
            >
              Done
            </button>
          </div>
        )}
        {screen === "link" && (
          <div className="flex gap-2 px-5 py-3" style={{ borderTop: "1px solid var(--border)" }}>
            <button
              type="button"
              className="btn-primary"
              style={{ flex: 1 }}
              onClick={onClose}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
